export const THREAD_LAYOUT_MAX_THREADS = 12
export const THREAD_LAYOUT_MAX_SEGMENTS_PER_THREAD = 2500

type SegmentGeometry = {
    height: number
    layoutSignature: string
}

type ThreadLayoutLedger = {
    geometry: Map<string, SegmentGeometry>
    lastAccessedAt: number
}

const ledgers = new Map<string, ThreadLayoutLedger>()
let accessSequence = 0

const nextAccess = () => {
    accessSequence += 1
    return accessSequence
}

function getOrCreateLedger(threadKey: string) {
    let ledger = ledgers.get(threadKey)
    if (!ledger) {
        ledger = { geometry: new Map(), lastAccessedAt: nextAccess() }
        ledgers.set(threadKey, ledger)
    }
    ledger.lastAccessedAt = nextAccess()
    return ledger
}

function evictOldLedgers(activeThreadKey?: string) {
    if (ledgers.size <= THREAD_LAYOUT_MAX_THREADS) return
    const candidates = Array.from(ledgers.entries())
        .filter(([threadKey]) => threadKey !== activeThreadKey)
        .sort(([, left], [, right]) => left.lastAccessedAt - right.lastAccessedAt)

    while (ledgers.size > THREAD_LAYOUT_MAX_THREADS && candidates.length > 0) {
        ledgers.delete(candidates.shift()![0])
    }
}

export function readThreadSegmentGeometry(
    threadKey: string,
    segmentKey: string,
    layoutSignature: string,
): number | null {
    const ledger = ledgers.get(threadKey)
    const geometry = ledger?.geometry.get(segmentKey)
    if (!ledger || !geometry || geometry.layoutSignature !== layoutSignature) return null
    ledger.lastAccessedAt = nextAccess()
    ledger.geometry.delete(segmentKey)
    ledger.geometry.set(segmentKey, geometry)
    return geometry.height
}

export function rememberThreadSegmentGeometry(
    threadKey: string,
    segmentKey: string,
    layoutSignature: string,
    height: number,
) {
    if (!threadKey || !segmentKey || !Number.isFinite(height) || height <= 0) return

    const ledger = getOrCreateLedger(threadKey)
    ledger.geometry.delete(segmentKey)
    ledger.geometry.set(segmentKey, { height, layoutSignature })
    while (ledger.geometry.size > THREAD_LAYOUT_MAX_SEGMENTS_PER_THREAD) {
        const oldestKey = ledger.geometry.keys().next().value
        if (oldestKey == null) break
        ledger.geometry.delete(oldestKey)
    }
    evictOldLedgers(threadKey)
}

export function resetThreadLayoutMemoryForTests() {
    ledgers.clear()
    accessSequence = 0
}
